/**
 * The two adjustments every bank statement carries — interest earned and bank
 * service charges — plus any ad-hoc extras, entered while starting a
 * reconciliation (#428).
 *
 * Pure module: the heuristic that guesses a sensible GL from the chart, the
 * signing rule, and the `source_ref` tag that makes these entries findable
 * later all live here so they can be tested without a form or a database.
 */

export type AdjustmentSlot = 'interest' | 'bankCharge' | 'extra';

export interface GlCodeLike {
	id: string;
	code: string;
	name: string;
	accountType?: string | null;
	active?: boolean;
}

/**
 * Signed cents for one adjustment row, from the amount the user typed.
 *
 * Interest earned always ADDS to the bank and bank charges always SUBTRACT, so
 * those two fields take a plain positive amount off the statement and we apply
 * the sign — asking a property manager to type "-2.00" for a service charge is
 * a needless trap. An `extra` row keeps the sign the user typed, matching the
 * existing Add-adjustment modal ("negative for withdrawals").
 */
export const signedAdjustmentCents = (slot: AdjustmentSlot, enteredCents: number): number => {
	if (!Number.isFinite(enteredCents)) return 0;
	const magnitude = Math.abs(enteredCents);
	if (slot === 'interest') return magnitude;
	if (slot === 'bankCharge') return -magnitude;
	return enteredCents;
};

/**
 * Scheme every reconciliation-created `source_ref` starts with. Exported so the
 * bank register can recognise — and hide — its own internal tags rather than
 * printing `reconciliation:<uuid>:bankCharge` in a user-facing Num column.
 */
export const ADJUSTMENT_SOURCE_REF_SCHEME = 'reconciliation:';

/** Prefix identifying every journal entry a given reconciliation created. */
export const adjustmentSourceRefPrefix = (reconciliationId: string): string =>
	`${ADJUSTMENT_SOURCE_REF_SCHEME}${reconciliationId}:`;

/**
 * `source_ref` for one adjustment entry.
 *
 * Unique per (reconciliation, slot, index) on purpose. `postLedgerEntry` keys
 * idempotency on (source, source_ref), so a distinct ref per row means
 * re-submitting the same Start form can't double-post — while a SHARED ref
 * would make the second row silently no-op (the trap the Add-adjustment modal
 * avoids by leaving sourceRef unset entirely).
 */
export const adjustmentSourceRef = (
	reconciliationId: string,
	slot: AdjustmentSlot,
	index = 0,
): string => `${adjustmentSourceRefPrefix(reconciliationId)}${slot}${slot === 'extra' ? `-${index}` : ''}`;

/**
 * Net effect on ONE bank account of a posted adjustment entry: debits minus
 * credits over just the lines that hit that bank. Used to tell the user what
 * they're about to leave posted (or void) when they stop a reconciliation.
 *
 * Lines are filtered by `bankAccountId` rather than summed wholesale, because
 * the counter-GL line is the mirror image and would net the entry to zero.
 */
export const adjustmentNetCents = (
	entry: { lines?: readonly { bankAccountId?: string | null; debitCents?: number; creditCents?: number }[] | null },
	bankAccountId: string,
): number => (entry.lines ?? []).reduce(
	(sum, l) => (l.bankAccountId === bankAccountId
		? sum + (l.debitCents ?? 0) - (l.creditCents ?? 0)
		: sum),
	0,
);

const norm = (s: string | null | undefined): string => (s ?? '').toLowerCase();

/**
 * Best guess at the interest-income GL from a global chart.
 *
 * Wants income, not expense: a chart typically holds several LOAN INTEREST
 * expense accounts alongside the one INTEREST INCOME account, and matching on
 * the word "interest" alone picks a loan-interest expense roughly at random.
 * So: account type must be income, and anything reading like borrowing cost is
 * rejected outright.
 */
export const guessInterestGl = (codes: readonly GlCodeLike[]): GlCodeLike | undefined => {
	const candidates = codes.filter((c) => {
		if (c.active === false) return false;
		if (norm(c.accountType) !== 'inc') return false;
		const n = norm(c.name);
		if (!n.includes('interest')) return false;
		return !n.includes('loan') && !n.includes('expense') && !n.includes('penalty');
	});
	// Lowest code wins so the pick is deterministic rather than insertion-ordered.
	return [...candidates].sort((a, b) => a.code.localeCompare(b.code))[0];
};

/**
 * Best guess at the bank-service-charge GL from a global chart.
 *
 * Requires BOTH a bank-ish word and a cost-ish word, so a plain "Fees" or
 * "Bank" account can't win. Expense types only — EXP and EXEXP (the QuickBooks
 * "other expense" type both appear in this chart).
 */
export const guessBankChargeGl = (codes: readonly GlCodeLike[]): GlCodeLike | undefined => {
	const candidates = codes.filter((c) => {
		if (c.active === false) return false;
		const t = norm(c.accountType);
		if (t !== 'exp' && t !== 'exexp') return false;
		const n = norm(c.name);
		const bankish = n.includes('bank') || n.includes('service');
		const costish = n.includes('fee') || n.includes('charge');
		return bankish && costish;
	});
	return [...candidates].sort((a, b) => a.code.localeCompare(b.code))[0];
};

export interface ResolvedAdjustmentGls {
	interestGlCodeId?: string;
	bankChargeGlCodeId?: string;
}

/**
 * Resolve the GL for each slot: the bank account's remembered choice wins, and
 * the chart heuristic only fills a blank. A remembered id that no longer exists
 * in the chart (deactivated, deleted) is discarded rather than sent to the
 * server, where it would fail GL validation.
 */
export const resolveAdjustmentGls = (
	codes: readonly GlCodeLike[],
	remembered: { interestGlCodeId?: string | null; bankChargeGlCodeId?: string | null },
): ResolvedAdjustmentGls => {
	const known = new Set(codes.filter((c) => c.active !== false).map((c) => c.id));
	const pick = (
		rememberedId: string | null | undefined,
		guess: () => GlCodeLike | undefined,
	): string | undefined => {
		if (rememberedId && known.has(rememberedId)) return rememberedId;
		return guess()?.id;
	};
	return {
		interestGlCodeId: pick(remembered.interestGlCodeId, () => guessInterestGl(codes)),
		bankChargeGlCodeId: pick(remembered.bankChargeGlCodeId, () => guessBankChargeGl(codes)),
	};
};
